// ReportListSidebar.js
import React, { useRef, useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  FaSync, FaTimes, FaSearch, FaSearchMinus, FaFileExcel,
  FaHistory, FaInbox, FaList, FaArchive, FaArrowUp, FaCog
} from 'react-icons/fa';
import Button from '../components/buttons/Button';
import Input from '../components/inputs/Input';
import Picker from '../components/inputs/Picker';
import Toggle from '../components/Toggle/Toggle';
import { parseReportSections } from './parseReportSections';
import exportAdvancedSearchToExcel from './exportAdvancedSearchToExcel';
import { isCapacitor } from '../utils/platform';
import { 
  groupReportsByDay, 
  shouldShowUnreadIndicator, 
  isReportProcessing, 
  isReportClickable,
  getReportContent
} from './reportListUtils';
import { ReportTypeNames } from '../models/ReportTypeNames';
import { useAuth } from '../context/AuthContext';
import HoverTooltip from '../components/tooltips/HoverTooltip';

export default function ReportListSidebar({
  reports,
  filteredReports,
  selectedReport,
  onReportClick,
  onArchiveReport,
  onDeleteReport,
  onContextMenu,
  isRefreshing,
  onRefresh,
  loading,
  refreshCooldown,
  searchQuery,
  onSearchChange,
  reportTypeFilter,
  onReportTypeFilterChange,
  dateFilter,
  onDateFilterChange,
  showArchived,
  onShowArchivedChange,
  isEnterpriseAdmin,
  extractedReportTypes,
  onExtractTypes,
  showNotification,
  isSidebarVisible,
  onMouseLeave,
  isElectron,
  currentVisibleGroup,
  statusCheckingReports,
  statusCheckResults,
  isAdvancedSearchActive,
  onSubmitAdvancedSearch,
  onStopAdvancedSearch,
  onShowLiveDictations
}) {
  const { profile } = useAuth();
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const [availableReportTypes, setAvailableReportTypes] = useState([]);
  const scrollableRef = useRef(null);
  const searchInputRef = useRef(null);

  // Focus search input when search bar is shown and not on Capacitor
  useEffect(() => {
    if (showSearchBar && !isCapacitor && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchBar]);

  const [showAdvancedOverlay, setShowAdvancedOverlay] = useState(false);
  const [advQ, setAdvQ] = useState('');
  const [advStartDate, setAdvStartDate] = useState('');
  const [advEndDate, setAdvEndDate] = useState('');
  const [advReportType, setAdvReportType] = useState('');
  const [advIncludeEnterprise, setAdvIncludeEnterprise] = useState(false);
  const [advGeneratedBy, setAdvGeneratedBy] = useState('');
  const [advLimit, setAdvLimit] = useState('');
  const [advQTerms, setAdvQTerms] = useState([]);
  const [advQOperator, setAdvQOperator] = useState('AND');
  const [showExportOverlay, setShowExportOverlay] = useState(false);
  const [exportSectionTitles, setExportSectionTitles] = useState([]);
  const [selectedExportTitles, setSelectedExportTitles] = useState([]);
  const [exportAsCSV, setExportAsCSV] = useState(false);
  const [exportFullText, setExportFullText] = useState(false);
  const [extractPerSection, setExtractPerSection] = useState({});
  const [numbersPerSection, setNumbersPerSection] = useState({});
  const [numbersModePerSection, setNumbersModePerSection] = useState({});
  const [extractBirthDate, setExtractBirthDate] = useState(false);
  const [includeStatistics, setIncludeStatistics] = useState(false);

  const handleExportAdvancedResultsExcel = () => {
    // stub: implement export to Excel later
  };

  // Build unique section titles when opening export overlay
  useEffect(() => {
    if (!showExportOverlay) return;
    try {
      const reportsSource = filteredReports || [];
      const seen = new Set();
      const titles = [];
      for (const rep of reportsSource) {
        const content = getReportContent(rep) || '';
        const sections = parseReportSections(content);
        for (const sec of sections) {
          const t = (sec?.title || '').trim();
          if (t && !seen.has(t)) {
            seen.add(t);
            titles.push(t);
          }
        }
      }
      setExportSectionTitles(titles);
      setSelectedExportTitles(titles);
    } catch (e) {
      // Fallback to empty
      setExportSectionTitles([]);
      setSelectedExportTitles([]);
    }
  }, [showExportOverlay, filteredReports]);

  const toggleSelectTitle = (title) => {
    setSelectedExportTitles((prev) => {
      if (prev.includes(title)) return prev.filter((t) => t !== title);
      return [...prev, title];
    });
  };

  const groupedReports = groupReportsByDay(filteredReports, dateFilter);

  // Get report types from profile (no API call)
  useEffect(() => {
    if (profile) {
      setAvailableReportTypes(profile.reportTypeFavorites.getFavorites());
    } else {
      // Fallback to extracted types from current reports
      setAvailableReportTypes(extractedReportTypes);
    }
  }, [extractedReportTypes]);

  useEffect(() => {
    const handleScroll = (e) => {
      if (e.target.scrollTop > 200) {
        setShowScrollTopButton(true);
      } else {
        setShowScrollTopButton(false);
      }
    };

    const scrollEl = scrollableRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const scrollToTop = () => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {isCapacitor && showScrollTopButton && isSidebarVisible && (
        <button
          onClick={scrollToTop}
          className={isCapacitor ? "mobile-scroll-to-top-btn" : "scroll-to-top-btn"}
        >
          <FaArrowUp />
        </button>
      )}

      <div
        className={`${isCapacitor ? 'mobile-sidebar-container' : 'sidebar-container'} ${isSidebarVisible || !selectedReport ? '' : isCapacitor ? 'mobile-collapsed' : 'collapsed'} ${
            isCapacitor ? 'mobile-web-mode' : 'web-mode'
        }`}
        onMouseLeave={onMouseLeave}
      >
        <div className={isCapacitor ? "mobile-sidebar-fixed" : "sidebar-fixed"}>
          <div className={isCapacitor ? "mobile-topbar-container" : "topbar-container"}>
            <div className={isCapacitor ? "mobile-filter-container" : "filter-container"}>
            {isAdvancedSearchActive ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                <Button
                  variant="primary"
                  onClick={onStopAdvancedSearch}
                  collapseOnSmallScreen={false}
                >
                  Arrêter la recherche avancée
                </Button>
                <Button
                  variant="success"
                  icon={FaFileExcel}
                  onClick={() => setShowExportOverlay(true)}
                  collapseOnSmallScreen={false}
                >
                  Exporter en Excel
                </Button>
              </div>
            ) : (
              <div className={isCapacitor ? "mobile-top-row" : "top-row"}>
                <div className={isCapacitor ? "mobile-refresh-button-container" : "refresh-button-container"}>
                  <Button
                    type="fixed"
                    icon={FaSync}
                    variant={refreshCooldown ? "secondary" : "primary"}
                    collapseOnSmallScreen={false}
                    onClick={onRefresh}
                    disabled={refreshCooldown}
                    isSpinning={refreshCooldown}
                  >
                    { 'Rafraîchir'}
                  </Button>
                </div>


                <Button
                  icon={showSearchBar ? FaSearchMinus : FaSearch}
                  variant="secondary"
                  onClick={() => setShowSearchBar(!showSearchBar)}
                  collapseOnSmallScreen={false}
                />
              </div>
            )}

              {showSearchBar  && (
                <div className={isCapacitor ? "mobile-search-row" : "search-row"}>
                  <div className={isCapacitor ? "mobile-search-container" : "search-container"}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => onSearchChange(e.target.value)}
                    />
                    {(searchQuery || reportTypeFilter || dateFilter !== 'all') && (
                      <Button
                        icon={FaTimes}
                        variant="secondary"
                        onClick={() => {
                          onSearchChange('');
                          onReportTypeFilterChange('');
                          onDateFilterChange('all');
                        }}
                      />
                    )}
                  </div>
                  <div className={isCapacitor ? "mobile-date-filter-container" : "date-filter-container"}>
                    <div className={isCapacitor ? "mobile-picker-with-icon" : "picker-with-icon"}>
                      <Picker
                        id="report-type-filter"
                        value={reportTypeFilter}
                        onChange={(e) => onReportTypeFilterChange(e.target.value)}
                      >
                        <option value="">Tous les modèles</option>
                        {availableReportTypes.map((type) => (
                          <option key={type} value={type}>
                            {ReportTypeNames.formatDisplayName(type)}
                          </option>
                        ))}
                      </Picker>
                    </div>
                  </div>

                     {/* Advanced Search Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: '8px', gap: '10px', width: '100%', overflowX: 'auto' }}>
                  {!isAdvancedSearchActive && !isCapacitor && (
                    <Button
                      variant="info"
                      onClick={() => setShowAdvancedOverlay(true)}
                      collapseOnSmallScreen={false}
                    >
                      Recherche avancée
                    </Button>
                  )}
                  {!isAdvancedSearchActive && isElectron && (
                    <Button
                      variant="primary"
                      onClick={onShowLiveDictations}
                      collapseOnSmallScreen={false}
                    >
                      Voir les dictées Lives
                    </Button>
                  )}
                </div>
                </div>
              )}
      {!isAdvancedSearchActive && (
              <div className="archive-and-date-container">
                <div className={showArchived ? "current-group archived" : "current-group"}>
                  <select
                    value={dateFilter}
                    onChange={(e) => onDateFilterChange(e.target.value)}
                  >
                    <option value="today">Récents</option>
                    <option value="3days">3 jours</option>
                    <option value="1week">7 jours</option>
                    <option value="all">Tous</option>
                  </select>
                  <span>{currentVisibleGroup}</span>
                </div>

                <div className={isCapacitor ? "mobile-archive-toggle-container" : "archive-toggle-container"}>
                  <HoverTooltip text={!showArchived ? "Afficher les rapports archivés" : "Afficher les rapports non archivés"} position="bottom">
                  <Button 
                  variant={showArchived ? "info" : "white"}
                  icon={showArchived ? FaInbox : FaArchive}
                  onClick={() => onShowArchivedChange(!showArchived)}
                  />
                  </HoverTooltip>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>

        <div className={isCapacitor ? "mobile-report-list-scrollable" : "report-list-scrollable"} ref={scrollableRef}>
          {Object.keys(groupedReports).map((dateKey, idx) => (
            <div key={idx} className={isCapacitor ? "mobile-report-group" : "report-group"}>
              <h3 className={"report-group-title"}>{dateKey}</h3>
              <ul>
                {groupedReports[dateKey].map((rep) => {
                  const isChecking = statusCheckingReports && statusCheckingReports.has(rep.id);
                  const statusResult = statusCheckResults && statusCheckResults.get(rep.id);
                  const isUnread = shouldShowUnreadIndicator(rep);
                  const isProcessing = isReportProcessing(rep);
                  
                  // Determine indicator color: yellow for processing unread, green for other unread
                  const unreadIndicatorClass = isUnread 
                    ? (isProcessing ? 'unread-processing' : 'unread-completed')
                    : '';
                  
                  return (
                  <li
                    key={rep.id}
                    className={`${isCapacitor ? 'mobile-report-item' : 'report-item'} ${
                      selectedReport && selectedReport.id === rep.id
                        ? isCapacitor 
                          ? showArchived 
                            ? 'mobile-selected-report archived' 
                            : 'mobile-selected-report'
                          : showArchived
                            ? 'selected-report archived'
                            : 'selected-report'
                        : ''
                    } ${isReportProcessing(rep) ? 'processing' : ''} ${!isReportClickable(rep) ? 'disabled' : ''} ${
                      isChecking ? 'checking-status' : ''
                    } ${
                      statusResult && statusResult.type === 'success' && !rep.isRead ? 'status-success' : ''
                    } ${
                      statusResult && statusResult.type === 'error' ? 'status-error' : ''
                    } ${
                      unreadIndicatorClass
                    }`}
                    onClick={() => isReportClickable(rep) && onReportClick(rep)}
                    style={{ cursor: isReportClickable(rep) ? 'pointer' : 'not-allowed' }}
                  >
                    <div className="report-header">
                      <p>
                        <strong>
                          {ReportTypeNames.formatDisplayName(rep.reportType)} - {format(new Date(rep.createdAt), 'HH:mm')}
                          {isReportProcessing(rep) && (
                            <FaCog className="processing-icon" />
                          )}
                        </strong>
                      </p>
                    </div>
                    <p className={isCapacitor ? "mobile-report-preview" : "report-preview"}>
                      {isChecking 
                        ? 'Vérification du statut...'
                        : isReportProcessing(rep) 
                          ? 'Traitement en cours...' 
                          : getReportContent(rep) ? getReportContent(rep).slice(0, isCapacitor ? 200 : 80) + '...' : ''
                      }
                    </p>
                    
                    {/* Status message */}
                    {statusResult && (
                      <div className={`status-message ${statusResult.type}`}>
                        {statusResult.message}
                      </div>
                    )}
                    
                    {/* Try again button for failed status checks */}
                    {statusResult && statusResult.type === 'error' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onReportClick(rep);
                        }}
                        className="try-again-button"
                      >
                        Réessayer
                      </button>
                    )}
                    
                    {isReportClickable(rep) && !isChecking && (
                      <>
                        {!rep.isArchived ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onArchiveReport(rep.id);
                              showNotification(`${ReportTypeNames.formatDisplayName(rep.reportType)} archivé.`, 'info');
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onContextMenu(e, rep.id);
                            }}
                            className={isCapacitor ? "mobile-archive-button" : "archive-button"}
                          >
                            <FaArchive /> Archiver
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteReport(rep.id);
                            }}
                            className={isCapacitor ? "mobile-delete-button" : "delete-button"}
                          >
                            <FaTimes /> Supprimer
                          </button>
                        )}
                      </>
                    )}
                  </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {filteredReports.length === 0 && (
            <div className="no-reports-container">
              <p>
                {dateFilter === 'today' 
                  ? "Aucun rapport aujourd'hui." 
                  : isAdvancedSearchActive ? "Aucun rapport trouvé." : "Aucun rapport disponible."}
              </p>
              {(dateFilter === 'today') && (
                <Button
                  icon={FaHistory}
                  variant="secondary"
                  onClick={() => {
                    onDateFilterChange('all');
                  }}
                >
                  Afficher plus
                </Button>
              )}
            </div>
          )}
        </div>
        
        {!isCapacitor && showScrollTopButton && (
          <button
            onClick={scrollToTop}
            className={"scroll-to-top-btn"}
          >
            <FaArrowUp />
          </button>
        )}
      </div>

      {/* Advanced Search Overlay */}
      {showAdvancedOverlay && (
        <div className="report-type-overlay" onClick={() => setShowAdvancedOverlay(false)}>
          <div className="report-type-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '720px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Recherche avancée</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={{ gridColumn: '1 / span 2' }}>
                <label style={{ display: 'block', marginBottom: '6px' }}>Template</label>
                <Picker
                  id="adv-report-type"
                  value={advReportType}
                  onChange={(e) => setAdvReportType(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">Tous les modèles</option>
                  {availableReportTypes.map((type) => (
                    <option key={type} value={type}>{ReportTypeNames.formatDisplayName(type)}</option>
                  ))}
                </Picker>
              </div>
              <div style={{ gridColumn: '1 / span 2' }}>
                <Input type="text" label="Contenu" value={advQ} onChange={(e) => setAdvQ(e.target.value)} placeholder="Rechercher dans le contenu..." style={{ width: '100%' }} />
              </div>
              <div style={{ gridColumn: '1 / span 2' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  {advQTerms.length > 0 && (
                    <select value={advQOperator} onChange={(e) => setAdvQOperator(e.target.value)} style={{ padding: '6px 8px' }}>
                      <option value="AND">ET</option>
                      <option value="OR">OU</option>
                    </select>
                  )}
                  
                  <Button
                    variant="secondary"
                    onClick={() => setAdvQTerms((prev) => [...prev, ''])}
                  >
                    Ajouter un terme de recherche
                  </Button>
                  
                </div>
                {advQTerms.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {advQTerms.map((term, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <Input
                            type="text"
                            label={`Terme ${idx + 1}`}
                            value={term}
                            onChange={(e) => setAdvQTerms((prev) => prev.map((t, i) => i === idx ? e.target.value : t))}
                            placeholder={`Terme ${idx + 1}`}
                          />
                        </div>
                        <Button
                          variant="secondary"
                          onClick={() => setAdvQTerms((prev) => prev.filter((_, i) => i !== idx))}
                        >
                          Retirer
                        </Button>
                       
                      </div>
                       
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px' }}>Date de début</label>
                <input
                  type="date"
                  value={advStartDate}
                  onChange={(e) => setAdvStartDate(e.target.value)}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px' }}>Date de fin</label>
                <input
                  type="date"
                  value={advEndDate}
                  onChange={(e) => setAdvEndDate(e.target.value)}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>


              <div style={{ gridColumn: '1 / span 2' }}>
                <Input type="number" label="Limite" min="1" value={advLimit} onChange={(e) => setAdvLimit(e.target.value)} placeholder="ex: 20" style={{ width: '100%' }} />
              </div>

              {isEnterpriseAdmin && (
                <>
                  <div style={{ gridColumn: '1 / span 2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Toggle checked={advIncludeEnterprise} onChange={setAdvIncludeEnterprise} label={"Inclure les rapports de l'organisation"} />
                  </div>
                  {advIncludeEnterprise && (
                    <div style={{ gridColumn: '1 / span 2' }}>
                      <Input
                        type="text"
                        label="Généré par (optionnel)"
                        value={advGeneratedBy}
                        onChange={(e) => setAdvGeneratedBy(e.target.value)}
                        placeholder={"Nom de l'utilisateur (exact)"}
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Button
                variant="primary"
                type="inversed"
                collapseOnSmallScreen={false}
                onClick={() => {
                  setAdvQ('');
                  setAdvStartDate('');
                  setAdvEndDate('');
                  setAdvReportType('');
                  setAdvIncludeEnterprise(false);
                  setAdvGeneratedBy('');
                  setAdvLimit('');
                  setAdvQTerms([]);
                  setAdvQOperator('AND');
                  if (isAdvancedSearchActive) {
                    onStopAdvancedSearch();
                  }
                }}
              >
                Réinitialiser
              </Button>
              <Button
                variant="secondary"
                collapseOnSmallScreen={false}
                onClick={() => setShowAdvancedOverlay(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const qTermsClean = advQTerms.map((t) => t.trim()).filter(Boolean);
                  const limitNum = advLimit ? Number(advLimit) : undefined;
                  onSubmitAdvancedSearch({ q: advQ.trim(), qTerms: qTermsClean, qOperator: advQOperator, startDate: advStartDate, endDate: advEndDate, reportType: advReportType, includeEnterprise: advIncludeEnterprise, generatedBy: advGeneratedBy.trim(), limit: limitNum });
                  setShowAdvancedOverlay(false);
                }}
                disabled={(
                  !advQ.trim() &&
                  advQTerms.filter((t) => t.trim()).length === 0 &&
                  !advStartDate &&
                  !advEndDate &&
                  !advReportType &&
                  !(isEnterpriseAdmin && (advIncludeEnterprise || advGeneratedBy.trim()))
                )}
              >
                Valider
              </Button>
            </div>
          </div>
        </div>
      )}

      {showExportOverlay && (
        <div className="report-type-overlay" onClick={() => setShowExportOverlay(false)}>
          <div className="report-type-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '720px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Exporter les rapports</h3>
            <div style={{ marginTop: '8px' }}>
              <p>Sélectionnez le format et les colonnes de l'export du fichier Excel.</p>
              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column', marginBottom: '8px' }}>
                <Toggle checked={exportAsCSV} onChange={setExportAsCSV} label={"Exporter en CSV"} />
                <Toggle checked={exportFullText} onChange={setExportFullText} label={"Exporter le rapport complet dans une colonne"} />
                <Toggle checked={extractBirthDate} onChange={setExtractBirthDate} label={"Extraire date de naissance du rapport"} />
                {Object.values(numbersPerSection).some(Boolean) && <Toggle checked={includeStatistics} onChange={setIncludeStatistics} label={"Inclure statistiques (moyenne, SD, median, IQR 25/75)"} disabled={!Object.values(numbersPerSection).some(Boolean)} />}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px', marginBottom: '8px' }}>
                <Button
                  variant="secondary"
                  onClick={() => setSelectedExportTitles(exportSectionTitles)}
                >
                  Tout sélectionner
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setSelectedExportTitles([])}
                >
                  Tout désélectionner
                </Button>
                <span style={{ alignSelf: 'center', color: '#666' }}>{selectedExportTitles.length}/{exportSectionTitles.length} sélectionnées</span>
              </div>
              {!exportFullText && (
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee', padding: '8px', borderRadius: '6px', opacity: exportFullText ? 0.4 : 1 }}>
                {exportSectionTitles.length === 0 && (
                  <p style={{ color: '#666' }}>Aucune section trouvée dans les résultats.</p>
                )}
                {exportSectionTitles.map((title) => (
                  <div key={title} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                    <label style={{ flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={selectedExportTitles.includes(title)}
                        onChange={() => !exportFullText && toggleSelectTitle(title)}
                        style={{ marginRight: '8px' }}
                      />
                      {title}
                    </label>
                    <label style={{ whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={!!extractPerSection[title]}
                        onChange={(e) => setExtractPerSection((prev) => ({ ...prev, [title]: e.target.checked }))}
                        disabled={exportFullText || !selectedExportTitles.includes(title)}
                        style={{ marginRight: '6px' }}
                      />
                      Extraire les données de la section
                    </label>
                    <label style={{ whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={!!numbersPerSection[title]}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setNumbersPerSection((prev) => ({ ...prev, [title]: checked }));
                          if (checked && !numbersModePerSection[title]) {
                            setNumbersModePerSection((prev) => ({ ...prev, [title]: 'all' }));
                          }
                        }}
                        disabled={exportFullText || !selectedExportTitles.includes(title)}
                        style={{ marginRight: '6px' }}
                      />
                      Extraire les chiffres de la section
                    </label>
                    {numbersPerSection[title] && (
                      <div style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: '#555' }}>Mode:</span>
                        <select
                          value={numbersModePerSection[title] || 'all'}
                          onChange={(e) => setNumbersModePerSection((prev) => ({ ...prev, [title]: e.target.value }))}
                          style={{ padding: '4px 6px' }}
                        >
                          <option value="all">Extraire tous</option>
                          <option value="min">Plus petit</option>
                          <option value="max">Plus grand</option>
                          <option value="avg">Moyenne</option>
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Button
                variant="secondary"
                collapseOnSmallScreen={false}
                onClick={() => setShowExportOverlay(false)}
              >
                Annuler
              </Button>
              <Button
                variant="success"
                icon={FaFileExcel}
                onClick={async () => {
                  try {
                    if (exportAsCSV) {
                      const blob = await exportAdvancedSearchToExcel(
                        filteredReports || [],
                        selectedExportTitles || [],
                        { fullTextMode: exportFullText, extractConfig: extractPerSection, numbersConfig: numbersPerSection, numbersModeConfig: numbersModePerSection, extractBirthDate, includeStatistics, exportAsCSV: true }
                      );
                      if (blob && blob instanceof Blob) {
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        const ts = new Date();
                        const pad = (n) => String(n).padStart(2, '0');
                        const fname = `rapports_recherche_avancee_${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}.csv`;
                        link.href = url;
                        link.download = fname;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      }
                    } else {
                      const blob = await exportAdvancedSearchToExcel(
                        filteredReports || [],
                        selectedExportTitles || [],
                        { fullTextMode: exportFullText, extractConfig: extractPerSection, numbersConfig: numbersPerSection, numbersModeConfig: numbersModePerSection, extractBirthDate, includeStatistics }
                      );
                      if (blob && blob instanceof Blob) {
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        const ts = new Date();
                        const pad = (n) => String(n).padStart(2, '0');
                        const fname = `rapports_recherche_avancee_${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}.xlsx`;
                        link.href = url;
                        link.download = fname;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      }
                    }
                    setShowExportOverlay(false);
                  } catch (e) {
                    // optional: you can show a notification via props.showNotification if provided
                  }
                }}
                disabled={!exportFullText && selectedExportTitles.length === 0}
              >
                Exporter
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}